import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useRevealWage } from "../../../../hooks/useRevealWage";
import { DEFAULT_TARGET_PPD } from "../../../../pages/Settings/DepartmentConfiguration/utils";
import { BADGE, FONT_SIZE, INK, NEUTRAL } from "../../../../theme/tokens";
import type { DayItem, DayStats } from "../../../../utils/models";
import { getChipColor } from "../../../../utils/utilFunctions";
import NurseHoursChip from "../../../staffingCheaps/HoursChip";

type DayHeaderProps = { dayItem: DayItem; onClick: () => void };

export const DayHeader: FC<DayHeaderProps> = ({ dayItem, onClick }) => {
	const { chosenDepartment } = useHospitalData();
	const canViewWage = useRevealWage();

	const isActualLoaded = useMemo<boolean>(() => dayItem.date < DateTime.now().startOf("day") && !!dayItem.shiftsActual.length, [dayItem]);

	const activeStats = useMemo<DayStats>(
		() => (isActualLoaded ? dayItem.dayStatsActual : dayItem.dayStats),
		[isActualLoaded, dayItem.dayStatsActual, dayItem.dayStats],
	);

	return (
		<Box
			sx={{
				gap: 0.5,
				display: "flex",
				flexDirection: "column",
				cursor: "pointer",
				alignItems: "center",
				height: {
					xs: "fit-content",
					xl: "100px",
				},
				py: {
					xs: "8px",
					xl: "16px",
				},
				"&:hover": {
					bgcolor: NEUTRAL[50],
				},
				transition: "background-color 0.2s ease",
			}}
			onClick={onClick}
		>
			<Typography
				sx={(theme) => ({
					fontWeight: 600,
					fontSize: FONT_SIZE.EMPHASIS,
					color: INK.SECONDARY,
					borderRadius: BADGE.PILL_RADIUS_FULL,
					px: "10px",
					bgcolor: dayItem.date.toFormat("yyyy-MM-dd") === DateTime.now().toFormat("yyyy-MM-dd") ? alpha(theme.palette.primary.light, 0.4) : "inherit",
				})}
			>
				{dayItem.date.toFormat("ccc d")}
			</Typography>
			<Box sx={{ justifyContent: "center" }}>
				<NurseHoursChip colors={getChipColor(dayItem.dayStats, chosenDepartment)} dayStats={activeStats} isActual={isActualLoaded} date={dayItem.date} />
			</Box>
			{activeStats.ppd != null && canViewWage && (
				<Box sx={{ display: "flex", gap: 1 }}>
					<Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS, color: INK.SECONDARY }} data-testid={`ppd-actual-${dayItem.date.toISODate()}`}>
						${activeStats.ppd.toFixed(2)}
					</Typography>
					{!!chosenDepartment.ppd && chosenDepartment.ppd > DEFAULT_TARGET_PPD && (
						<Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS, color: alpha(INK.SECONDARY, 0.5) }}>
							/ ${chosenDepartment.ppd.toFixed(2)}
						</Typography>
					)}
				</Box>
			)}
		</Box>
	);
};
